import { useState, useEffect } from 'react'
import { journalistService, type Journalist } from '../lib/journalistService'
import { useEventYear } from '../components/EventYearContext'
import { useAuth } from '../components/AuthContext'

export function useJournalists() {
  const { currentYear } = useEventYear()
  const { user: currentUser } = useAuth()
  const [journalists, setJournalists] = useState<Journalist[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const isJournalist = currentUser?.role === 'journalist'

  // Load data on mount and when year changes
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        setError(null)

        if (isJournalist && currentUser?.email) {
          // Journalists only get their own record
          const own = await journalistService.getByEmail(currentUser.email)
          setJournalists(own ? [own] : [])
        } else {
          const journalistsData = await journalistService.getAll(currentYear)
          setJournalists(journalistsData)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load journalists')
        console.error('Error loading journalists:', err)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [currentYear, isJournalist])

  const reloadData = async () => {
    try {
      setLoading(true)
      setError(null)

      if (isJournalist && currentUser?.email) {
        const own = await journalistService.getByEmail(currentUser.email)
        setJournalists(own ? [own] : [])
      } else {
        const journalistsData = await journalistService.getAll(currentYear)
        setJournalists(journalistsData)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load journalists')
      console.error('Error loading journalists:', err)
    } finally {
      setLoading(false)
    }
  }

  const addJournalist = async (journalist: Omit<Journalist, 'id'>) => {
    try {
      const newJournalist = await journalistService.create(journalist, currentYear)
      if (newJournalist) {
        setJournalists(prev => [newJournalist, ...prev])
      }
      return newJournalist
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add journalist')
      throw err
    }
  }

  const bulkAddJournalists = async (journalistsData: Omit<Journalist, 'id'>[]) => {
    try {
      const newJournalists = await journalistService.bulkCreate(journalistsData, currentYear)

      // Reload all journalists to ensure we have the latest data
      const allJournalists = await journalistService.getAll(currentYear)
      setJournalists(allJournalists)

      return newJournalists
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk add journalists')
      throw err
    }
  }

  const bulkUpsertJournalists = async (journalistsData: Omit<Journalist, 'id'>[]) => {
    try {
      const result = await journalistService.bulkUpsert(journalistsData, currentYear)

      // Update local state with new and updated journalists
      setJournalists(prev => {
        const existingById = new Map(prev.map(j => [j.id, j]))

        result.updated.forEach(updated => {
          existingById.set(updated.id, { ...existingById.get(updated.id), ...updated })
        })

        const updatedExisting = Array.from(existingById.values())
        return [...result.added, ...updatedExisting]
      })

      return result
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk upsert journalists')
      throw err
    }
  }

  const updateJournalist = async (id: string, updates: Partial<Omit<Journalist, 'id'>>) => {
    try {
      const updatedJournalist = await journalistService.update(id, updates)
      if (updatedJournalist) {
        setJournalists(prev => prev.map(j => j.id === id ? updatedJournalist : j))
      }
      return updatedJournalist
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update journalist')
      throw err
    }
  }

  const deleteJournalist = async (id: string) => {
    try {
      const success = await journalistService.delete(id)
      if (success) {
        setJournalists(prev => prev.filter(journalist => journalist.id !== id))
      } else {
        throw new Error('Delete operation returned false')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete journalist')
      throw err
    }
  }

  const bulkDeleteJournalists = async (ids: string[]) => {
    try {
      await journalistService.bulkDelete(ids)
      setJournalists(prev => prev.filter(journalist => !ids.includes(journalist.id)))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk delete journalists')
      throw err
    }
  }

  const deleteAllJournalists = async () => {
    try {
      await journalistService.deleteAll()
      setJournalists([])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete all journalists')
      throw err
    }
  }

  return {
    journalists,
    loading,
    error,
    reloadData,
    addJournalist,
    bulkAddJournalists,
    bulkUpsertJournalists,
    updateJournalist,
    deleteJournalist,
    bulkDeleteJournalists,
    deleteAllJournalists
  }
}
